import React, { useEffect, useMemo, useRef, useState } from 'react'

import CheckIcon from '@mui/icons-material/Check'
import {
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
  useTheme,
} from '@mui/material'
import { useTranslation } from 'react-i18next'

import collabHorizontal from '../../assets/images/collab_horizontal.svg'
import collabHorizontalWhite from '../../assets/images/collab_horizontal_white.svg'
import gift100 from '../../assets/images/gift100.png'
import { TrackedLinkClickService } from '../../services/TrackedLinkClickService'
import { CustomButton } from '../CustomButton'
import { useJoyride } from '../Joyride/JoyrideProvider'
import { CustomModal } from '../Modal/CustomModal'

const BONUS_URL = 'https://go.nuvesto.com/visit/?bta=35074&brand=nuvesto'
const AUTO_OPEN_DELAY_MS = 30_000
export const EXCLUSIVE_BONUS_MODAL_OPEN_EVENT = 'exclusive-bonus-modal-open'
const EXCLUSIVE_BONUS_TRACKED_LINK = {
  linkKey: 'exclusive-bonus-nuvesto',
  linkLabel: 'Exclusive Bonus - Nuvesto',
  targetUrl: BONUS_URL,
  source: 'exclusive-bonus-modal',
}

export const openExclusiveBonusModal = () => {
  window.dispatchEvent(new CustomEvent(EXCLUSIVE_BONUS_MODAL_OPEN_EVENT))
}

type ExclusiveBonusModalProps = {
  userId?: string
  enabled?: boolean
}

export const ExclusiveBonusModal: React.FC<ExclusiveBonusModalProps> = ({
  userId,
  enabled = true,
}) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const { run: isJoyrideRunning } = useJoyride()
  const collabImage = theme.palette.mode === 'dark' ? collabHorizontalWhite : collabHorizontal

  const storageKey = useMemo(
    () => (userId ? `exclusive-bonus-modal-shown-${userId}` : ''),
    [userId],
  )

  const [open, setOpen] = useState(false)
  const remainingDelayRef = useRef(AUTO_OPEN_DELAY_MS)
  const countdownStartedAtRef = useRef<number | null>(null)

  useEffect(() => {
    if (!userId || !storageKey) {
      setOpen(false)
      return
    }

    const wasShown = localStorage.getItem(storageKey) === 'true'

    if (wasShown) {
      remainingDelayRef.current = 0
      return
    }

    if (!enabled || isJoyrideRunning || open) {
      return
    }

    countdownStartedAtRef.current = Date.now()
    const timeoutId = window.setTimeout(() => {
      remainingDelayRef.current = 0
      countdownStartedAtRef.current = null
      setOpen(true)
    }, remainingDelayRef.current)

    return () => {
      if (countdownStartedAtRef.current !== null) {
        const elapsed = Date.now() - countdownStartedAtRef.current
        remainingDelayRef.current = Math.max(0, remainingDelayRef.current - elapsed)
        countdownStartedAtRef.current = null
      }
      window.clearTimeout(timeoutId)
    }
  }, [enabled, isJoyrideRunning, open, storageKey, userId])

  useEffect(() => {
    if (isJoyrideRunning) {
      remainingDelayRef.current = AUTO_OPEN_DELAY_MS
      countdownStartedAtRef.current = null
    }
  }, [isJoyrideRunning])

  useEffect(() => {
    const handleOpenEvent = () => {
      setOpen(true)
    }

    window.addEventListener(EXCLUSIVE_BONUS_MODAL_OPEN_EVENT, handleOpenEvent)

    return () => {
      window.removeEventListener(EXCLUSIVE_BONUS_MODAL_OPEN_EVENT, handleOpenEvent)
    }
  }, [])

  const markAsShown = () => {
    if (storageKey) {
      localStorage.setItem(storageKey, 'true')
    }
  }

  const handleClose = () => {
    markAsShown()
    remainingDelayRef.current = 0
    setOpen(false)
  }

  const handleClaimBonus = () => {
    if (!BONUS_URL) {
      return
    }

    markAsShown()
    remainingDelayRef.current = 0
    setOpen(false)

    void TrackedLinkClickService.create(EXCLUSIVE_BONUS_TRACKED_LINK).catch(() => null)

    window.open(BONUS_URL, '_blank', 'noopener,noreferrer')
  }

  const modalPoints = [t('exclusiveBonusModal.noteTerms')]

  return (
    <CustomModal
      open={open}
      onClose={handleClose}
      title={String(t('exclusiveBonusModal.title', '🎉 Exclusive: Tradelytic x Nuvesto'))}
      dialogTransitionProps={{
        timeout: {
          enter: 500,
          exit: 200,
        },
      }}
      dialogTitleStyles={{
        pr: '45px',
      }}
      // showBodyDividers={false}
      // dialogTitleStyles={{
      //   padding: { xs: 2, sm: 3 },
      // }}
      modalBodyStyles={{
        width: { xs: 'auto', sm: 600 },
        maxWidth: '100%',
        display: 'flex',
        alignItems: { xs: 'center', sm: 'flex-start' },
        justifyContent: 'center',
        padding: { xs: 3 },
      }}
      showCancelButton={false}
      showSubmitButton={false}
    >
      <Stack spacing={{ xs: 3, sm: 2 }} alignItems="center" textAlign="center">
        <Box
          component="img"
          src={collabImage}
          alt="Tradelytic x Nuvesto"
          sx={{
            width: '100%',
            maxWidth: { xs: 400, sm: 370 },
            height: 'auto',
            // paddingY: { xs: 1, sm: 2 },
          }}
        />
        <Box
          component="img"
          src={gift100}
          alt="100% bonus"
          sx={{
            width: '100%',
            maxWidth: { xs: 180, sm: 150 },
            height: 'auto',
          }}
        />
        <Stack spacing={1} alignItems="center">
          <Stack direction="column" spacing={0.5} alignItems="center">
            <Typography
              sx={{
                color: theme.palette.text.primary,
              }}
              variant="h2"
            >
              {t('exclusiveBonusModal.headlineLine1')}{' '}
              <Typography component="span" variant="h2" sx={{ color: theme.palette.primary.main }}>
                {t('exclusiveBonusModal.headlineLine2')}
              </Typography>
            </Typography>
          </Stack>
          <Typography color="text.primary" sx={{ maxWidth: 420, fontSize: 16 }} variant="body1">
            {t('exclusiveBonusModal.description')}
          </Typography>
        </Stack>
        <List disablePadding sx={{ width: 'fit-content', maxWidth: '100%', mx: 'auto' }}>
          {modalPoints.map((point) => (
            <ListItem
              key={String(point)}
              sx={{ pl: 0, pr: 0, py: 0, alignItems: 'center', width: 'fit-content', mx: 'auto' }}
            >
              <ListItemIcon sx={{ minWidth: 25 }}>
                <Box
                  sx={{
                    width: 18,
                    height: 18,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: theme.palette.mode === 'dark' ? 'rgba(0, 128, 255, 0.18)' : '#E8F0FF',
                    color: theme.palette.primary.main,
                  }}
                >
                  <CheckIcon sx={{ fontSize: 12 }} />
                </Box>
              </ListItemIcon>
              <ListItemText
                primary={point}
                sx={{ flex: '0 0 auto' }}
                primaryTypographyProps={{ sx: { whiteSpace: 'normal' } }}
              />
            </ListItem>
          ))}
        </List>
        <CustomButton
          onClick={handleClaimBonus}
          disabled={!BONUS_URL}
          sx={{
            width: { xs: '100%', sm: 400 },
            mt: 1,
            alignSelf: 'center',
          }}
        >
          {String(t('exclusiveBonusModal.action.claim', 'Claim Your Bonus →'))}
        </CustomButton>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ maxWidth: 420, fontSize: 12, lineHeight: 1.4 }}
        >
          {t(
            'exclusiveBonusModal.disclaimer',
            '*100% deposit bonus is non-withdrawable and may only be used as trading margin.',
          )}
        </Typography>
      </Stack>
    </CustomModal>
  )
}
